import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ConfirmWithTypedNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Human-readable text the user must type exactly to confirm (e.g. founder full name). */
  expectedText: string;
  title: string;
  description: React.ReactNode;
  destructiveLabel?: string;
  isPending?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * Re-usable destructive-confirm dialog: "Type {name} to confirm" pattern.
 * Built for STA-743 admin "Offboard Immediately" — the strict equality on
 * `expectedText` is intentional, so a misclick on the wrong founder row
 * can't be confirmed by typing the wrong name.
 */
const ConfirmWithTypedNameDialog = ({
  open,
  onOpenChange,
  expectedText,
  title,
  description,
  destructiveLabel = "Confirm",
  isPending = false,
  onConfirm,
}: ConfirmWithTypedNameDialogProps) => {
  const [input, setInput] = useState("");

  // Reset input on open/close so a previous typing doesn't persist.
  useEffect(() => {
    if (!open) setInput("");
  }, [open]);

  const matches = input === expectedText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-2">
          <Label htmlFor="confirm-typed-name">
            Type <span className="font-semibold">{expectedText}</span> to confirm.
          </Label>
          <Input
            id="confirm-typed-name"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={expectedText}
            autoComplete="off"
          />
        </div>
        <DialogFooter className="gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!matches || isPending}
            onClick={() => onConfirm()}
          >
            {isPending ? "Working…" : destructiveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmWithTypedNameDialog;
